import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Save, ScanLine, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PpScannedList } from './PpScannedList';

// Batch scan: USNs are captured locally (instant). Counts per component come from the floor list loaded
// when the combo was picked; the server checks everything on Save.
export const PpBatchScanPanel = ({ session, floorMap, floorLoading, items, setItems, banner, submitting, onSave }) => {
  const [scanInput, setScanInput] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (!floorLoading) inputRef.current?.focus(); }, [floorLoading]);

  const components = session.components || [];
  const nameOf = (bc) => components.find((c) => c.component_barcode === bc)?.component_name || bc;
  const required = {};
  components.forEach((c) => { required[c.component_barcode] = (required[c.component_barcode] || 0) + (parseInt(c.quantity, 10) || 1); });
  const barcodes = Object.keys(required);
  const tally = {};
  items.forEach((it) => { if (it.component_barcode && !it.problem) tally[it.component_barcode] = (tally[it.component_barcode] || 0) + 1; });
  const perCombo = barcodes.map((bc) => Math.floor((tally[bc] || 0) / required[bc]));
  const minTally = perCombo.length ? Math.min(...perCombo) : 0;
  const isEven = perCombo.length > 0 && barcodes.every((bc) => (tally[bc] || 0) % required[bc] === 0) && perCombo.every((v) => v === minTally);
  const missing = barcodes.filter((bc) => (tally[bc] || 0) < required[bc]).map(nameOf);
  const flagged = items.filter((it) => it.problem || !it.component_barcode).length;
  const ready = !flagged && isEven && minTally > 0;

  const addCodes = (raw) => {
    const codes = raw.split(/[\s,;]+/).map((c) => c.trim()).filter(Boolean);
    if (!codes.length) return;
    const have = new Set(items.map((i) => i.usn));
    const fresh = [];
    const dups = [];
    codes.forEach((usn) => {
      if (have.has(usn)) { dups.push(usn); return; }
      have.add(usn);
      fresh.push({ usn, component_barcode: floorMap?.[usn] || null, problem: null });
    });
    if (dups.length) toast.error(dups.length === 1 ? `${dups[0]} already scanned` : `${dups.length} USNs already scanned`);
    if (fresh.length) setItems((p) => [...fresh.reverse(), ...p]);
  };

  const onSubmitScan = (e) => { e.preventDefault(); addCodes(scanInput); setScanInput(''); inputRef.current?.focus(); };

  return (
    <div className="p-4">
      <form onSubmit={onSubmitScan} className="flex gap-2 mb-2">
        <Input ref={inputRef} autoFocus disabled={floorLoading} placeholder={floorLoading ? 'Loading floor USNs…' : 'Scan component USNs one after another…'}
          value={scanInput} onChange={(e) => setScanInput(e.target.value)} onPaste={(e) => { const t = e.clipboardData.getData('text'); if (/[\s,;]/.test(t.trim())) { e.preventDefault(); addCodes(t); } }}
          data-testid="usn-input" className="flex-1" />
        <Button type="submit" disabled={floorLoading} data-testid="scan-btn"><ScanLine className="h-4 w-4 mr-1" /> Add</Button>
      </form>
      <p className="text-xs text-stone-500 mb-4" data-testid="floor-info">
        {floorLoading ? <><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Loading this combo's floor USNs…</>
          : floorMap ? `Batch scan: nothing is checked until you press Save. ${Object.keys(floorMap).length.toLocaleString('en-IN')} floor USNs loaded for instant counts.`
            : 'Batch scan: floor list could not be loaded — counts will show after Save.'}
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {barcodes.map((bc) => (
          <div key={bc} className="bg-stone-50 p-3 rounded border" data-testid={`tally-${bc}`}>
            <div className="text-xs text-stone-500">{nameOf(bc)}</div>
            <div className="text-2xl font-bold">{tally[bc] || 0}</div>
          </div>
        ))}
        <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
          <div className="text-xs text-emerald-700">Combos to save</div>
          <div className="text-2xl font-bold text-emerald-800" data-testid="combos-count">{isEven ? minTally : '—'}</div>
        </div>
      </div>

      {banner && <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="save-problem-banner">{banner}</div>}
      <PpScannedList items={items} nameOf={nameOf} onRemove={(usn) => setItems((p) => p.filter((i) => i.usn !== usn))} />

      <div className="mt-4 flex justify-end gap-2 flex-wrap items-center">
        {missing.length > 0 && items.length > 0 && <Badge className="bg-red-100 text-red-800" data-testid="missing-components-badge">Missing components: {missing.join(', ')}</Badge>}
        {!missing.length && !isEven && <Badge className="bg-amber-100 text-amber-800" data-testid="counts-uneven-badge">Counts uneven — keep scanning</Badge>}
        {flagged > 0 && (
          <Button variant="outline" className="border-red-300 text-red-700" onClick={() => setItems((p) => p.filter((i) => !i.problem && i.component_barcode))} data-testid="remove-problems-btn">
            <Trash2 className="h-4 w-4 mr-1" /> Remove {flagged} flagged USN(s)
          </Button>
        )}
        <Button onClick={onSave} disabled={submitting || !items.length} data-testid="submit-btn" className="bg-green-600 hover:bg-green-700">
          {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          {ready ? `Save ${minTally} Combo${minTally === 1 ? '' : 's'}` : 'Check & Save'}
        </Button>
      </div>
    </div>
  );
};
